/**
 * Token store — keeps the access token in module-level memory only.
 * Storing it in localStorage exposes it to XSS; memory storage prevents that.
 *
 * Session recovery: if the page is refreshed, the access token is gone.
 * AuthContext will call /auth/refresh using the refresh token stored in
 * sessionStorage. If that also fails, the user is redirected to login.
 * sessionStorage is scoped to the browser tab and cleared when it closes,
 * which is significantly safer than localStorage for sensitive tokens.
 */

const REFRESH_KEY = 'rentry_refresh';
const LOGIN_TIME_KEY = 'rentry_lt';

let _accessToken: string | null = null;

export const tokenStore = {
  /** Return the in-memory access token (null if not set or page was refreshed). */
  getAccess(): string | null {
    return _accessToken;
  },

  /** Store the access token in memory only. */
  setAccess(token: string): void {
    _accessToken = token;
    sessionStorage.setItem(LOGIN_TIME_KEY, Date.now().toString());
  },

  /** Return true if the 15-minute session window has elapsed. */
  isExpired(): boolean {
    const t = sessionStorage.getItem(LOGIN_TIME_KEY);
    if (!t) return false; // no record means a fresh memory-only session — let the 401 decide
    return Date.now() - parseInt(t, 10) > 900_000;
  },

  /** Refresh token stored in sessionStorage (tab-scoped, not persisted). */
  getRefresh(): string | null {
    return sessionStorage.getItem(REFRESH_KEY);
  },

  setRefresh(token: string): void {
    sessionStorage.setItem(REFRESH_KEY, token);
  },

  /** Clear all token state on logout. */
  clear(): void {
    _accessToken = null;
    sessionStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem(LOGIN_TIME_KEY);
  },
};
